import type {
  GroupByTimeGranularity,
  GetInteractionAggregatesReq,
  GetInteractionAggregatesReqAdditionalGroupBysItem,
  GetInteractionAggregatesReqFiltersItem,
  GetInteractionAggregatesReqGroupBy,
  TimeRangeConfig,
} from '@/api/generated/model';
import type { GenericFilter } from '@/comps/FilterPicker/FilterPicker';
import { isFilterDefined } from '@/models/common/filters';
import {
  SEGMENT_TREND_ROW_LIMIT,
  SPEND_BY_MODEL_ROW_LIMIT,
  type SpendGroupKind,
} from '@/domains/pages/ai-spend/constants';
import { MAX_DAILY_TREND_BUCKETS, type TrendQueryConfig } from '@/domains/pages/shared/trend-query';
import { buildSegmentGroupBy, type SegmentKind } from '@/domains/pages/shared/segment-kind';

export const AI_SPEND_AGGREGATE_VARIABLES = ['cost', 'n_segments', 'n_users'] as const;

export const AI_SPEND_TREND_VARIABLES = ['cost', 'n_segments'] as const;

export const AI_SPEND_DISTRIBUTION_VARIABLES = ['cost', 'roi_success_rate', 'n_segments', 'n_users'] as const;

export const AI_SPEND_TASK_MODEL_VARIABLES = ['cost', 'n_segments'] as const;

type BuildAiSpendAggregatesPayloadParams = {
  projectId: string;
  timeRange: TimeRangeConfig;
  filters: GenericFilter[];
  specialFilters: GenericFilter[];
};

type BuildAiSpendTrendAggregatesPayloadParams = {
  projectId: string;
  trendQuery: TrendQueryConfig;
  filters: GenericFilter[];
  specialFilters: GenericFilter[];
};

export function buildAiSpendOrgAggregatesPayload({
  projectId,
  timeRange,
  filters,
  specialFilters,
}: BuildAiSpendAggregatesPayloadParams): GetInteractionAggregatesReq {
  return {
    project_id: projectId,
    time_range: timeRange,
    filters: filters.filter(isFilterDefined) as GetInteractionAggregatesReqFiltersItem[],
    special_filters: specialFilters.filter(isFilterDefined) as GetInteractionAggregatesReq['special_filters'],
    variables: [...AI_SPEND_AGGREGATE_VARIABLES],
    calculate_previous: true,
    limit: 1,
  };
}

type BuildAiSpendGroupAggregatesPayloadParams = BuildAiSpendAggregatesPayloadParams & {
  kind: SpendGroupKind;
};

export function resolveSpendGroupBy(kind: SpendGroupKind): GetInteractionAggregatesReqGroupBy {
  if (kind === 'task') {
    return { kind: 'topic' };
  }
  if (kind === 'model') {
    return { kind: 'llm_model' };
  }
  return { kind };
}

export function buildAiSpendGroupAggregatesPayload({
  projectId,
  timeRange,
  filters,
  specialFilters,
  kind,
}: BuildAiSpendGroupAggregatesPayloadParams): GetInteractionAggregatesReq {
  return {
    project_id: projectId,
    time_range: timeRange,
    filters: filters.filter(isFilterDefined) as GetInteractionAggregatesReqFiltersItem[],
    special_filters: specialFilters.filter(isFilterDefined) as GetInteractionAggregatesReq['special_filters'],
    variables: [...AI_SPEND_DISTRIBUTION_VARIABLES],
    group_by: resolveSpendGroupBy(kind),
    order_by: { by: 'cost', direction: 'desc' },
    limit: 100,
  };
}

export function buildAiSpendTeamAggregatesPayload({
  projectId,
  timeRange,
  filters,
  specialFilters,
}: BuildAiSpendAggregatesPayloadParams): GetInteractionAggregatesReq {
  return {
    project_id: projectId,
    time_range: timeRange,
    filters: filters.filter(isFilterDefined) as GetInteractionAggregatesReqFiltersItem[],
    special_filters: specialFilters.filter(isFilterDefined) as GetInteractionAggregatesReq['special_filters'],
    variables: [...AI_SPEND_AGGREGATE_VARIABLES],
    group_by: { kind: 'team' },
    order_by: { by: 'cost', direction: 'desc' },
    limit: 100,
  };
}

export function buildAiSpendTrendAggregatesPayload({
  projectId,
  trendQuery,
  filters,
  specialFilters,
}: BuildAiSpendTrendAggregatesPayloadParams): GetInteractionAggregatesReq {
  return {
    project_id: projectId,
    time_range: trendQuery.time_range,
    filters: filters.filter(isFilterDefined) as GetInteractionAggregatesReqFiltersItem[],
    special_filters: specialFilters.filter(isFilterDefined) as GetInteractionAggregatesReq['special_filters'],
    variables: [...AI_SPEND_TREND_VARIABLES],
    group_by: { kind: 'time', granularity: trendQuery.granularity as GroupByTimeGranularity },
    limit: MAX_DAILY_TREND_BUCKETS,
  };
}

export function buildSegmentScopeFilter(kind: SegmentKind, name: string): GenericFilter | null {
  const trimmed = name.trim();
  if (!trimmed) {
    return null;
  }
  return {
    column: kind,
    values: [trimmed],
    is_exclude: false,
  };
}

/** One row per (period, segment): the cheapest/most-expensive band needs both dimensions at once. */
export function buildAiSpendSegmentTrendPayload({
  projectId,
  trendQuery,
  filters,
  specialFilters,
  kind,
}: BuildAiSpendTrendAggregatesPayloadParams & { kind: SegmentKind }): GetInteractionAggregatesReq {
  return {
    project_id: projectId,
    time_range: trendQuery.time_range,
    filters: filters.filter(isFilterDefined) as GetInteractionAggregatesReqFiltersItem[],
    special_filters: specialFilters.filter(isFilterDefined) as GetInteractionAggregatesReq['special_filters'],
    variables: [...AI_SPEND_TREND_VARIABLES],
    group_by: { kind: 'time', granularity: trendQuery.granularity as GroupByTimeGranularity },
    additional_group_bys: [buildSegmentGroupBy(kind) as GetInteractionAggregatesReqAdditionalGroupBysItem],
    order_by: { by: 'cost', direction: 'desc' },
    limit: SEGMENT_TREND_ROW_LIMIT,
  };
}

/** Task × model × provider. Provider is requested purely so the legend can show an authoritative vendor glyph — it never affects bar identity. */
export function buildAiSpendTaskModelPayload({
  projectId,
  timeRange,
  filters,
  specialFilters,
}: BuildAiSpendAggregatesPayloadParams): GetInteractionAggregatesReq {
  return {
    project_id: projectId,
    time_range: timeRange,
    filters: filters.filter(isFilterDefined) as GetInteractionAggregatesReqFiltersItem[],
    special_filters: specialFilters.filter(isFilterDefined) as GetInteractionAggregatesReq['special_filters'],
    variables: [...AI_SPEND_TASK_MODEL_VARIABLES],
    group_by: { kind: 'topic' },
    additional_group_bys: [{ kind: 'llm_model' }, { kind: 'llm_provider' }],
    order_by: { by: 'cost', direction: 'desc' },
    limit: SPEND_BY_MODEL_ROW_LIMIT,
  };
}
